/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import firebase from 'firebase/app';
import 'firebase/firestore';
import { useParams } from 'react-router-dom';
import ErrorPage from '@components/common/Error';
import LoadingPage from '@components/common/Loading';
import Center from '@components/common/alignment/Center';
import PageHeader from '@components/common/PageHeader';
import { useDocumentDataOnce, useDocumentOnce } from 'react-firebase-hooks/firestore';
import CourseDataInfoBox from '@components/instructor/course/coursedata/CourseDataInfoBox';
import CurriculumInfoBox from '@components/instructor/course/curriculum/CurriculumInfoBox';
import ChecklistInfoBox from '@components/instructor/course/checklist/ChecklistInfoBox';
import { mapFirestoreError } from '@util/mapFirebaseError';
import { InstructorAuthContext } from '@src/authContexts';
import { CourseData, FileInfo } from '@data/course/courseData';
import { CourseDataConverter } from '@data/course/CourseDataConverter';
import { courseDataValidationSchema } from '@data/course/courseDataValidationSchema';
import { validateCurriculum } from '@data/course/validateCurriculum';
import { validateInstructorInfo } from '@data/user/validateInstructorInfo';
import { ApprovalInfo } from '@data/course/approval/approval';

const converter = new CourseDataConverter();

const CourseStatusPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();

  const { user, userDoc } = useContext(InstructorAuthContext);

  const docRef = useMemo(
    () => firebase.firestore().collection('classes').doc(id).withConverter(converter),
    [id]
  );
  const [doc, loading, error] = useDocumentOnce<CourseData>(docRef);
  const docData = doc?.data();
  const [courseApproval, courseApprovalLoading, courseApprovalError] =
    useDocumentDataOnce<ApprovalInfo>(docData?.courseApprovalRef);
  const [curriculumApproval, curriculumApprovalLoading, curriculumApprovalError] =
    useDocumentDataOnce<ApprovalInfo>(docData?.curriculumApprovalRef);

  const [courseFrozen, setCourseFrozen] = useState(false);

  const [dataValid, setDataValid] = useState(false);
  const [curriculumAttached, setCurriculumAttached] = useState(false);

  const initialValidationComplete = useRef(false);

  // Validate when data first comes in so that the checklist is correct
  useEffect(() => {
    if (docData && !initialValidationComplete.current) {
      initialValidationComplete.current = true;
      courseDataValidationSchema.isValid(docData).then((valid) => {
        setDataValid(valid);
      });

      setCurriculumAttached(validateCurriculum(docData.curriculumFiles));
    }
  }, [docData]);

  // Set courseFrozen correctly
  // We can't do this via useState because docData may
  // update multiple times as the data loads in
  useEffect(() => {
    if (docData?.frozen !== undefined) {
      setCourseFrozen(docData.frozen);
    }
  }, [docData?.frozen]);

  const freezeCourse = useCallback(
    () =>
      docRef
        .update({ frozen: true })
        .then(() => {
          setCourseFrozen(true);
        })
        .catch((e: firebase.firestore.FirestoreError) => {
          throw new Error('An error ocurred while confirming your course. ' + mapFirestoreError(e));
        }),
    [docRef]
  );

  const updateCurriculum = useCallback(
    (files: FileInfo[]) => {
      console.log(files);
      return docRef
        .set({ curriculumFiles: files }, { merge: true })
        .catch((e: firebase.firestore.FirestoreError) => {
          throw new Error('An error ocurred saving your files. ' + mapFirestoreError(e));
        });
    },
    [docRef]
  );

  const updateCourse = useCallback(
    (course: Partial<CourseData>) =>
      docRef.set(course, { merge: true }).catch((e: firebase.firestore.FirestoreError) => {
        throw new Error('An error ocurred saving your edits. ' + mapFirestoreError(e));
      }),
    [docRef]
  );

  if (loading || courseApprovalLoading || curriculumApprovalLoading) {
    return <LoadingPage></LoadingPage>;
  }

  if (
    !doc ||
    !docData ||
    error ||
    courseApprovalError ||
    !courseApproval ||
    curriculumApprovalError ||
    !curriculumApproval
  ) {
    return <ErrorPage></ErrorPage>;
  }

  if (docData.archived) {
    return (
      <Center>
        <p className='text-5xl font-raleway text-coffee'>Sorry, this course is archived.</p>
      </Center>
    );
  }

  const {
    frozen,
    curriculumFiles,
    courseApprovalRef,
    curriculumApprovalRef,
    archived,
    instructorIds,
    ...formDataOnly
  } = docData;

  return (
    <div className='w-full h-full overflow-auto font-raleway'>
      <PageHeader text='COURSE STATUS'></PageHeader>
      <div className='grid grid-cols-3 gap-4 my-10 mx-14'>
        <ChecklistInfoBox
          courseFrozen={courseFrozen}
          validity={{
            courseData: dataValid,
            curriculum: curriculumAttached,
            accountInfo: validateInstructorInfo(userDoc.data(), user),
            workshop: userDoc.data()?.workshopAttended ?? false,
          }}
          courseApproval={courseApproval}
          curriculumApproval={curriculumApproval}
          onConfirm={freezeCourse}
        />
        <CurriculumInfoBox
          courseFrozen={courseFrozen}
          initialFiles={docData.curriculumFiles}
          onChange={updateCurriculum}
          updateValidity={setCurriculumAttached}
        />
        <CourseDataInfoBox
          courseFrozen={courseFrozen}
          initialData={formDataOnly}
          onChange={updateCourse}
          updateValidity={setDataValid}
        />
      </div>
    </div>
  );
};

export default CourseStatusPage;
